/**
 * EntityCardPhoto — the fleet-card photo area. FT-436 (2026-08-08).
 *
 * Renders, in priority order:
 *   1. the entity's PROFILE image (uploaded photo OR picked placeholder), synced
 *      via the reused photo pipeline (useEntityProfilePhoto), OR
 *   2. a legacy `photoUrl` passed on the entity (back-compat), OR
 *   3. a "No photo yet" placeholder.
 *
 * The photo area is ALWAYS a plain, non-interactive display — tapping it triggers
 * the card's primary action (open vessel) via the full-card overlay.
 * Pic-edit lives on the separate 3-dot (⋯) corner icon (CardSettingsMenu). The
 * camera-affordance "Tap to set image" hint is therefore also removed — see the
 * 3-dot icon for that. Crew and permitted users alike see the same inert display.
 *
 * Additive: this REPLACES the static CardPhoto in FleetLanding but preserves its
 * exact visual (w-full h-32, rounded-top, placeholder styling) so mobile 390px +
 * tablet layout — and the known bottom-nav clearance — are unchanged.
 */

import React from 'react';
import { Camera } from 'lucide-react';
import { useEntityProfilePhoto } from './useEntityProfilePhoto';

const EntityCardPhoto = ({
  entityId,
  entityName,
  legacyPhotoUrl,      // optional back-compat url on the entity object
  fallbackIcon: FallbackIcon,
  capturedBy,
}) => {
  const { photoUrl } = useEntityProfilePhoto(entityId, capturedBy);

  const url = photoUrl || legacyPhotoUrl || null;

  if (url) {
    return (
      <img
        src={url}
        alt={entityName ? `${entityName} photo` : 'Photo'}
        className="w-full h-32 object-cover rounded-t-xl bg-gray-100"
        loading="lazy"
        data-testid={`card-photo-img-${entityId}`}
      />
    );
  }

  return (
    <div
      className="w-full h-32 flex flex-col items-center justify-center gap-1 rounded-t-xl bg-gradient-to-b from-slate-50 to-slate-100 text-slate-300"
      data-testid="card-photo-placeholder"
    >
      <FallbackIcon className="w-8 h-8" aria-hidden="true" />
      <span className="flex items-center gap-1 text-xs text-slate-400">
        <Camera className="w-3 h-3" aria-hidden="true" />
        No photo yet
      </span>
    </div>
  );
};

export default EntityCardPhoto;
